import type {
  Category,
  ConsentKitApi,
  ConsentRecord,
  ConsentEvent,
  ConsentListener,
  ConsentState,
  Decision,
  ResolvedRegion,
  UserConfig,
} from "./types";
import { VERSION } from "./core/defaults";
import { resolveConfig } from "./core/resolve-config";
import { baselineDecision, grantedCategories } from "./core/categories";
import { ConsentStore } from "./core/consent-state";
import { EventBus } from "./core/events";
import { resolveRegion } from "./core/region";
import { deleteCookiesMatching } from "./core/storage";
import { installBlocking, type Blocker } from "./blocking/installer";
import { DEFAULT_LOCALE, getDictionary, resolveLocale } from "./i18n";
import {
  REGISTRY,
  pushConsentDefault,
  pushConsentUpdate,
  resolveVendors,
  usesGoogleTags,
} from "./vendors/registry";
import { ConsentUi } from "./ui/root";
import { clearPlaceholders, mountPlaceholders } from "./ui/placeholder";

/**
 * Boot order matters more than anything else in this file. The guards and the Consent
 * Mode default have to be in place before a single third-party tag gets a chance to run,
 * which is why they happen synchronously in the constructor while the UI waits for the
 * document body.
 *
 * The region resolves first, because it decides what the *undecided* state is: everything
 * off under the DSGVO, everything the site enabled under US law. Every layer below — the
 * store, the blocker's `isGranted`, the Consent Mode default — reads that one baseline, so
 * the regimes never need parallel code paths.
 */
export class ConsentKit implements ConsentKitApi {
  readonly version = VERSION;

  private readonly config;
  private readonly resolvedRegion: ResolvedRegion;
  private readonly locale;
  private readonly dictionary;
  private readonly vendors;
  private readonly baseline: Decision;
  private readonly store: ConsentStore;
  private readonly bus = new EventBus();
  private readonly blocker: Blocker;
  private ui: ConsentUi | null = null;

  constructor(script: HTMLScriptElement | null, global?: UserConfig) {
    this.config = resolveConfig(script, global);
    if (isLocalhost() && !("debug" in (global ?? {}))) this.config.debug = true;

    this.resolvedRegion = resolveRegion(this.config);
    this.locale = resolveLocale(
      this.config.locale,
      this.resolvedRegion.region === "us" ? "en" : DEFAULT_LOCALE,
    );
    this.dictionary = getDictionary(this.locale, this.config.texts);
    this.vendors = resolveVendors(this.config.vendors, this.config.debug);

    this.baseline = baselineDecision(this.config, this.resolvedRegion);
    this.store = new ConsentStore(this.config, this.locale, this.resolvedRegion, this.baseline);

    // The default must be pushed before any Google tag evaluates its own.
    if (usesGoogleTags(this.vendors)) {
      pushConsentDefault(this.baseline, this.resolvedRegion.regime);
    }

    // The guards go in under every regime. In the opt-out one the baseline already grants
    // the enabled categories, so they pass everything through and only start holding things
    // back the moment the visitor opts out — or a GPC signal did it for them.
    this.blocker = installBlocking(this.config, this.vendors, REGISTRY, (category) =>
      this.store.has(category),
    );

    const restored = this.store.restore();

    // Unconditional: the sweep records markup-tagged elements whatever the decision is,
    // because their URL sits in `data-ck-src` where the browser will never find it. Under
    // the opt-out regime that is the only thing standing between a tagged tag and never
    // running at all.
    this.blocker.release(grantedCategories(this.store.state.decision));

    if (restored) {
      if (usesGoogleTags(this.vendors)) pushConsentUpdate(this.store.state.decision);
    } else if (this.resolvedRegion.gpc) {
      // An honoured GPC signal is a real opt-out and has to be provable, so it is written
      // as a record like any other. It matches the baseline the store already holds, which
      // is what keeps this from registering as a revocation and reloading the page.
      this.store.decide(this.baseline, "gpc");
      this.log("Global Privacy Control signal honoured", this.config.optOutCategories);
    }

    if (this.config.onChange) this.bus.on("change", this.config.onChange);
    whenBodyReady(() => this.mountUi(restored));
  }

  show(): void {
    this.ui?.showBanner();
    this.bus.emit("show", this.state);
  }

  showPreferences(): void {
    this.ui?.showPreferences();
    this.bus.emit("show", this.state);
  }

  hide(): void {
    this.ui?.hideBanner();
    this.ui?.closePreferences();
    this.bus.emit("hide", this.state);
  }

  acceptAll(): void {
    this.applyDecision(this.decisionFor(this.config.categories.enabled), "accept-all");
  }

  rejectAll(): void {
    this.applyDecision(this.decisionFor([]), "reject-all");
  }

  accept(categories: Exclude<Category, "necessary">[]): void {
    this.applyDecision(this.decisionFor(categories), "custom");
  }

  /**
   * The "Do Not Sell or Share My Personal Information" action. Narrower than `rejectAll()`
   * on purpose: it switches off exactly what the statutes call sale, sharing and targeted
   * advertising, and leaves categories the visitor never objected to running.
   *
   * Safe to call in the EU too — it just denies the same categories — so a footer link can
   * be wired up once for a site that serves both.
   */
  optOut(): void {
    const decision = { ...this.store.state.decision };
    for (const category of this.config.optOutCategories) decision[category] = false;
    this.applyDecision(decision, "opt-out");
  }

  get(): ConsentState {
    return this.store.state;
  }

  has(category: Category): boolean {
    return this.store.has(category);
  }

  region(): ResolvedRegion {
    return { ...this.resolvedRegion };
  }

  /** Wipes the stored decision and re-asks. Blocked items stay blocked until re-granted. */
  reset(): void {
    this.store.reset();
    this.ui?.hideRevisit();
    this.ui?.closePreferences();
    this.ui?.showBanner();
    this.bus.emit("change", this.state);
  }

  on(event: ConsentEvent, listener: ConsentListener): void {
    this.bus.on(event, listener);
  }

  off(event: ConsentEvent, listener: ConsentListener): void {
    this.bus.off(event, listener);
  }

  private get state(): ConsentState {
    return this.store.state;
  }

  private mountUi(restored: boolean): void {
    this.ui = new ConsentUi({
      config: this.config,
      t: this.dictionary,
      locale: this.locale,
      region: this.resolvedRegion,
      vendors: this.vendors,
      currentDecision: () => this.store.state.decision,
      onDecision: (decision, method) => this.applyDecision(decision, method),
    });

    if (isPreviewMode()) this.ui.showBanner();
    else if (restored || !this.hasSomethingToConsentTo()) this.ui.showRevisit();
    else this.ui.showBanner();

    this.mountPlaceholders();
    if (this.config.debug) this.blocker.audit();
    this.bus.emit("ready", this.state);
  }

  /**
   * A site that loads no non-essential vendor and has had nothing held back is not
   * required to interrupt anyone, and a banner asking permission for nothing is itself
   * misleading. The guards stay installed either way, so the banner appears the moment
   * something actually needs consent.
   *
   * The same test decides whether the US notice appears: notice at collection is owed for
   * data that is actually collected, and a site collecting none owes none.
   */
  private hasSomethingToConsentTo(): boolean {
    if (this.config.alwaysShowBanner) return true;
    if (this.vendors.length > 0) return true;
    if ((this.config.customBlocklist ?? []).length > 0) return true;
    if (this.blocker.context.items.all().length > 0) return true;

    if (this.config.debug) {
      console.info(
        "[consentkit] no vendors configured and nothing was blocked — banner suppressed. " +
          "Set alwaysShowBanner to override.",
      );
    }
    return false;
  }

  private applyDecision(decision: Decision, method: ConsentRecord["method"]): void {
    const change = this.store.decide(decision, method);

    this.blocker.release(change.granted);
    if (usesGoogleTags(this.vendors)) pushConsentUpdate(change.next);
    this.runVendorHooks(change.granted, change.revoked);

    this.ui?.hideBanner();
    this.ui?.closePreferences();
    this.ui?.showRevisit();
    clearPlaceholders();
    this.mountPlaceholders();

    this.bus.emit("change", this.state);

    if (change.revoked.length > 0) this.handleRevocation(change.revoked);
  }

  /**
   * A script that is already executing cannot be unloaded, so revoking a previously
   * granted category means clearing its cookies and reloading — anything less would
   * leave the visitor tracked after they said stop.
   */
  private handleRevocation(revoked: Category[]): void {
    const patterns = this.blocker.cookiePatterns
      .filter((entry) => revoked.includes(entry.category))
      .map((entry) => entry.pattern);
    if (patterns.length > 0) deleteCookiesMatching(patterns);

    if (this.config.reloadOnRevoke) window.location.reload();
  }

  private runVendorHooks(granted: Category[], revoked: Category[]): void {
    for (const vendor of this.vendors) {
      if (granted.includes(vendor.category)) vendor.onGrant?.();
      if (revoked.includes(vendor.category)) vendor.onRevoke?.();
    }
  }

  private mountPlaceholders(): void {
    if (!this.ui) return;
    mountPlaceholders(this.blocker.context.items.all(), {
      t: this.dictionary,
      tokensCss: this.ui.tokensCss,
      vendorFor: (url) => this.vendors.find((vendor) => vendor.domains.some((d) => url.includes(d))),
      onLoadOnce: (item) => this.blocker.releaseOne(item),
      onAllowCategory: (category) => {
        const decision = { ...this.store.state.decision, [category]: true };
        this.applyDecision(decision, "custom");
      },
    });
  }

  private decisionFor(categories: readonly Exclude<Category, "necessary">[]): Decision {
    return {
      necessary: true,
      functional: categories.includes("functional"),
      analytics: categories.includes("analytics"),
      marketing: categories.includes("marketing"),
    };
  }

  private log(...args: unknown[]): void {
    if (this.config.debug) console.info("[consentkit]", ...args);
  }
}

function whenBodyReady(callback: () => void): void {
  if (document.body) {
    callback();
    return;
  }
  document.addEventListener("DOMContentLoaded", callback, { once: true });
}

/**
 * `?ck-preview` forces the banner open on any page that already loads the bundle, so you
 * can check wording, theme and placement on the real site without a redeploy.
 *
 * It only affects what is *shown*: a stored decision is left untouched, and clicking a
 * button still records a real decision. Nothing here can be triggered without the visitor
 * putting it in their own URL.
 */
function isPreviewMode(): boolean {
  return new URLSearchParams(window.location.search).has("ck-preview");
}

function isLocalhost(): boolean {
  const host = window.location.hostname;
  return host === "localhost" || host === "127.0.0.1" || host.endsWith(".local");
}
